import { genderSelectOptions } from '@/lib/configs';
import { onboardingDataValues, onboardingConstants } from '@/lib/constants';
import { formatPostalCode, formatDate, formatPhone, formatNumberToCurrencyString } from '@/lib/utils';

const {
  DIRECT_DEPOSIT_BANK_NAME,
  DIRECT_DEPOSIT_ACCOUNT_NAME,
  DIRECT_DEPOSIT_ACCOUNT_TYPE,
  DIRECT_DEPOSIT_ROUTING_NUMBER,
  DIRECT_DEPOSIT_ACCOUNT_NUMBER,
  DIRECT_DEPOSIT_CONFIRM_ACCOUNT_NUMBER,
  USE_PREVIOUS_W9_NAME,
  REP_EXPERIENCES_DATA_NAME,
  COMPANY_YEARS_SOLD,
  COMPANY_ACCOUNT_NUMBERS,
  COMPANY_NAME,
  LAST_INDUSTRY_NAME,
  SOURCE_OF_DISCOVERY_NAME,
  CURRENT_SITUATION_NAME,
  LAST_INDUSTRY_OTHER_NAME,
  CUSTOMER_FACING_PHONE_NUMBER_NAME,
  FIRST_NAME,
  LAST_NAME,
  FULL_NAME,
  DATE_OF_BIRTH,
  GENDER,
  PRONOUN,
  ADDRESS_CITY,
  ADDRESS_STATE,
  ADDRESS_ZIP,
  EXPERIENCE,
  ADDRESS_COUNTRY,

  NEEDS_HOUSING,
  HOUSING_TYPE,
  NUM_OF_ROOMS,
  REP_ACKNOWLEDGMENT,
  ROOMMATE_REQUEST,
  TENTATIVE_KNOCKING_START_DATE,
  TENTATIVE_KNOCKING_END_DATE,
  ACTUAL_START_DATE,
  ACTUAL_END_DATE,
  RENT_TIER,
  OPT_IN,
  PRESEASON_HOUSING_TYPE,
  PRESEASON_NUM_OF_ROOMS,
  PRESEASON_TENTATIVE_KNOCKING_START_DATE,
  PRESEASON_TENTATIVE_KNOCKING_END_DATE,
  PRESEASON_ACTUAL_START_DATE,
  PRESEASON_ACTUAL_END_DATE,
  POSTSEASON_HOUSING_TYPE,
  POSTSEASON_NUM_OF_ROOMS,
  POSTSEASON_TENTATIVE_KNOCKING_START_DATE,
  POSTSEASON_TENTATIVE_KNOCKING_END_DATE,
  POSTSEASON_ACTUAL_START_DATE,
  POSTSEASON_ACTUAL_END_DATE,

  DRIVER_LICENSE_NUMBER,
  DRIVER_LICENSE_EXPIRATION_DATE,
  DRIVER_LICENSE_STATE_ISSUED,
  SOCIAL_SECURITY_NUMBER,
  LIMITED_LIABILITY_COMPANY,
  EMPLOYER_IDENTIFICATION_NUMBER,
  SOLICITOR_PERMIT_NUMBER,
  SOLICITOR_EXPIRATION_DATE,

  SALES_RANK,
  HAS_CV_BASED_PAY,
  UPFRONT_PAY_NAME,
  RENT_DEDUCTION_NAME,
  UNIFORM_DEDUCTION_NAME,
  RENT_DEDUCTION_REASON_NAME,

  PENDING_APPROVAL_ITEMS_NAME,
  PROFILE_PICTURE_URL_NAME,
  PASSPORT_PICTURE_URL_NAME,
  DRIVER_LICENSE_URL_NAME,
  SOCIAL_SECURITY_URL_NAME,
  PROFILE_PICTURE_LOCAL_FILE,
  PASSPORT_PICTURE_LOCAL_FILE,
  DRIVER_LICENSE_LOCAL_FILE,
  SOCIAL_SECURITY_PICTURE_LOCAL_FILE,
} = onboardingConstants;

export const parseRepExperiences = (data) => {
  return data?.map((item) => ({
    setId: `${item.id}`,
    id: item.id,
    [COMPANY_YEARS_SOLD]: item?.['years_sold'] ? item['years_sold'] : '',
    [COMPANY_ACCOUNT_NUMBERS]: item?.['number_of_accounts'] ? item['number_of_accounts'] : '',
    [COMPANY_NAME]: item?.['sales_company'] ? item['sales_company'] : '',
    [LAST_INDUSTRY_NAME]: item?.['last_industry'] ? item['last_industry'] : '',
    [LAST_INDUSTRY_OTHER_NAME]: item?.['last_industry_other'] ? item['last_industry_other'] : '',
  }));
};

export const normalizeIncomingData = (data) => {
  const placeholderArr = ['', ''];
  const height = data?.['height'] ? data['height'].match(/\d+/g) : placeholderArr;
  const formattedMainZip = data?.['permanent_zip']
    ? formatPostalCode(data?.['permanent_zip'])
    : '';
  const formattedCurrentZip = data?.['zip']
    ? formatPostalCode(data?.['zip'])
    : '';

  const parseEmployment = (data) => {
    return data?.map((item) => ({
      setId: `${item.id}`,
      id: item.id,
      employerName: item?.['employer'] ? item['employer'] : '',
      employerStartDate: item?.['service_from'] ? formatDate(item['service_from']) : '',
      employerEndDate: item?.['service_to'] ? formatDate(item['service_to']) : '',
    }));
  };

  const parseReferences = (data) => {
    return data?.map((item) => ({
      setId: `${item.id}`,
      id: item.id,
      referenceName: item?.['name'] ? item['name'] : '',
      referenceRelation: item?.['relation'] ? item['relation'] : '',
      referencePhoneNumber: item?.['phone'] ? formatPhone(item['phone']) : '',
    }));
  };

  const employmentHistory = data?.['employment']
    ? parseEmployment(data['employment'])
    : [];

  const referencesHistory = data?.['references']
    ? parseReferences(data['references'])
    : [];

  const experiences = data?.['experiences']
    ? parseRepExperiences(data['experiences'])
    : [];

  return {
    userId: data?.['user_id'] ? data?.['user_id'] : '',
    repId: data?.['id'] ? data?.['id'] : '', // TODO: seems not to be in use as of now. See normalizeIncomingData() call in src/redux/reps/sagas.js::requestRepAsContactSaga()
    recruitId: data?.['recruit_id'] ? data?.['recruit_id'] : '',
    email: data?.['email'] ? data?.['email'] : '',

    onboarded: Boolean(data?.['onboarded']),
    submitted: Boolean(data?.['is_submitted']),
    approved: Boolean(data?.['is_approved']),

    // Personal Info

    [FIRST_NAME]: data?.['first_name'] ? data['first_name'] : '',
    [LAST_NAME]: data?.['last_name'] ? data['last_name'] : '',
    [FULL_NAME]: data?.['preferred_name'] ? data['preferred_name'] : '',
    [DATE_OF_BIRTH]: data?.['dob'] ? formatDate(data?.['dob']) : '',
    [GENDER]: genderSelectOptions.find(({ value }) => value === data?.['gender'] )?.value ?? '',
    [PRONOUN]: data?.['pronoun_id'] ? data['pronoun_id'] : '',
    [EXPERIENCE]: data?.['experience'] ? data['experience'].toString() : '',
    mobile: data?.['mobile'] ? formatPhone(data['mobile']) : '',
    [CUSTOMER_FACING_PHONE_NUMBER_NAME]: data?.['customer_facing_mobile'] ? formatPhone(data['customer_facing_mobile']) : '',
    profilePicture: data?.['profilePicture'] ? data['profilePicture'] : '',
    profileImg: data?.['profileImg'] ? data['profileImg'] : '',
    isMarried: data?.['marital_status'] ? data['marital_status'] : '',
    [SALES_RANK]: data?.['sales_rank_id'] ? data['sales_rank_id'] : '',
    [HAS_CV_BASED_PAY]: String(data?.['has_cv_based_pay']) ?? onboardingDataValues.NULL_VALUE,
    [UPFRONT_PAY_NAME]: formatNumberToCurrencyString(data?.['upfront_pay'] ?? 0),
    [RENT_DEDUCTION_NAME]: formatNumberToCurrencyString(data?.['rent_deduction'] ?? 0, 2),
    [UNIFORM_DEDUCTION_NAME]: formatNumberToCurrencyString(data?.['uniform_deduction'] ?? 0),
    [RENT_DEDUCTION_REASON_NAME]: '',
    spouseFirstName: data?.['spouse_name'] ? data['spouse_name'] : '',
    spouseLastName: data?.['spouse_last_name'] ? data['spouse_last_name'] : '',
    emergencyContactName: data?.['emergency_contact_name']
      ? data['emergency_contact_name']
      : '',
    emergencyContactPhoneNumber: data?.['emergency_phone_number']
      ? formatPhone(data['emergency_phone_number'])
      : '',
    addressOne: data?.['permanent_address'] ? data['permanent_address'] : '',
    [ADDRESS_CITY]: data?.['permanent_city'] ? data['permanent_city'] : '',
    [ADDRESS_STATE]: data?.['permanent_state'] ? data['permanent_state'] : '',
    [ADDRESS_ZIP]: formattedMainZip,
    [ADDRESS_COUNTRY]: String(data?.['permanent_country'] ?? ''),
    isDifferentAddress: data?.['is_different_address']
      ? data['is_different_address']
      : '',
    currentAddressOne: data?.['address1'] ? data['address1'] : '',
    currentAddressCity: data?.['city'] ? data['city'] : '',
    currentAddressState: data?.['state'] ? data['state'] : '',
    currentAddressZip: formattedCurrentZip,
    currentAddressCountry: String(data?.['country'] ?? ''),
    [SOCIAL_SECURITY_NUMBER]: data?.['ss'] ? data['ss'] : '',
    [DRIVER_LICENSE_NUMBER]: data?.['drivers_license_number']
      ? data['drivers_license_number']
      : '',
    [DRIVER_LICENSE_STATE_ISSUED]: data?.['state_issued']
      ? data['state_issued']
      : '',
    driverLicenseCountryIssued: String(data?.['country_issued'] ?? ''),
    [DRIVER_LICENSE_EXPIRATION_DATE]: data?.['drivers_license_expiration_date']
      ? formatDate(data['drivers_license_expiration_date'])
      : '',
    facebookLink: data?.['facebook_username'] ?? '',
    linkedinLink: data?.['linkedin_username'] ?? '',
    twitterLink: data?.['twitter_username'] ?? '',
    instagramLink: data?.['instagram_username'] ?? '',
    status: data?.['status'] ?? '',
    team: data?.['team'] ?? '',
    userCreatedAt: data?.['user_created_at'] ?? '',

    // Housing and Vehicle

    expectedArrivalDate: data?.['arrival_date']
      ? formatDate(data?.['arrival_date'])
      : '',
    [TENTATIVE_KNOCKING_START_DATE]: data?.['start_date']
      ? formatDate(data?.['start_date'])
      : '',
    [TENTATIVE_KNOCKING_END_DATE]: data?.['end_date']
      ? formatDate(data?.['end_date'])
      : '',
    [ACTUAL_START_DATE]: data?.['actual_start_date']
      ? formatDate(data?.['actual_start_date'])
      : '',
    [ACTUAL_END_DATE]: data?.['actual_end_date']
      ? formatDate(data?.['actual_end_date'])
      : '',
    [NEEDS_HOUSING]: data?.['opt_in'] ? onboardingDataValues.YES_VALUE : onboardingDataValues.NO_VALUE,
    [HOUSING_TYPE]: String(data?.['apartment_status_id'] ?? ''),
    [NUM_OF_ROOMS]: String(data?.['no_of_rooms'] ?? ''),
    [ROOMMATE_REQUEST]: data?.['roommate_request'] ? data['roommate_request'] : '',
    [REP_ACKNOWLEDGMENT]: data?.['rep_acknowledgment']
      ? data['rep_acknowledgment']
      : false,
    addressHistoryName: data?.['resident_address']
      ? data['resident_address']
      : '',
    addressHistoryStartDate: data?.['resident_from'] ? formatDate(data['resident_from']) : '',
    addressHistoryEndDate: data?.['resident_to'] ? formatDate(data['resident_to']) : '',
    hasVehicle: data?.['is_personal_vehicle']
      ? data['is_personal_vehicle']
      : '',
    vehicleModel: data?.['vehicle_model'] ? data['vehicle_model'] : '',
    vehicleColor: data?.['vehicle_color'] ? data['vehicle_color'] : '',
    vehicleYear: data?.['purchase_year'] ? data['purchase_year'] : '',
    vehiclePlateNumber: data?.['license_plate_number']
      ? data['license_plate_number']
      : '',
    vehicleRegistrationState: data?.['license_state']
      ? data['license_state']
      : '',
    vehicleRegistrationCountry: String(data?.['license_country'] ?? ''),
    hasSegway: data?.['has_segway'] ? data['has_segway'] : '',
    [RENT_TIER]: data?.['rent_tier'] ? data['rent_tier'] : '',
    [OPT_IN]: Boolean(data?.['opt_in']),
    [PRESEASON_HOUSING_TYPE]: data?.pre_season?.['apartment_status_id'] ? data['pre_season']['apartment_status_id'] : '',
    [PRESEASON_NUM_OF_ROOMS]: data?.pre_season?.['number_of_rooms'] ? data['pre_season']['number_of_rooms'] : '',
    [PRESEASON_TENTATIVE_KNOCKING_START_DATE]: data?.pre_season?.['start_date'] ? data['pre_season']['start_date'] : '',
    [PRESEASON_TENTATIVE_KNOCKING_END_DATE]: data?.pre_season?.['end_date'] ? data['pre_season']['end_date'] : '',
    [PRESEASON_ACTUAL_START_DATE]: data?.pre_season?.['actual_start_date'] ? data['pre_season']['actual_start_date'] : '',
    [PRESEASON_ACTUAL_END_DATE]: data?.pre_season?.['actual_end_date'] ? data['pre_season']['actual_end_date'] : '',
    [POSTSEASON_HOUSING_TYPE]: data['post_season']['apartment_status_id'] ? data['post_season']['apartment_status_id'] : '',
    [POSTSEASON_NUM_OF_ROOMS]: data?.post_season?.['number_of_rooms'] ? data['post_season']['number_of_rooms'] : '',
    [POSTSEASON_TENTATIVE_KNOCKING_START_DATE]: data['post_season']['start_date'] ? data['post_season']['start_date'] : '',
    [POSTSEASON_TENTATIVE_KNOCKING_END_DATE]: data['post_season']['end_date'] ? data?.post_season?.['end_date'] : '',
    [POSTSEASON_ACTUAL_START_DATE]: data['post_season']['actual_start_date'] ? data?.post_season?.['actual_start_date'] : '',
    [POSTSEASON_ACTUAL_END_DATE]: data['post_season']['actual_end_date'] ? data?.post_season?.['actual_end_date'] : '',
    // Uniform

    shirtSize: data?.['polo_shirt_size'] ? data['polo_shirt_size'] : '',
    jacketSize: data?.['jacket_size'] ? data['jacket_size'] : '',
    waistSize: data?.['waist_size'] ? data['waist_size'] : '',
    hatSize: data?.['hat_size'] ? data['hat_size'] : '',
    shoeSize: data?.['shoe_size'] ? data['shoe_size'] : '',

    // Licensing

    race: data?.['ethnicity'] ? data['ethnicity'] : '',
    feet: height[0],
    inches: height[1],
    weight: data?.['weight'] ? data['weight'] : '',
    hairColor: data?.['hair_color'] ? data['hair_color'] : '',
    eyeColor: data?.['eye_color'] ? data['eye_color'] : '',
    cityOfBirth: data?.['place_of_birth'] ? data['place_of_birth'] : '',
    stateOfBirth: data?.['birth_state'] ? data['birth_state'] : '',
    countryOfBirth: String(data?.['birth_country'] ?? ''),
    stateOfBirthOther: data?.['other_birth_state'] ? data['other_birth_state'] : '',
    isUsCitizen: data?.['is_us_citizen'] ? data['is_us_citizen'] : '',
    hasVisibleMarkings: data?.['has_visible_markings'] ? data['has_visible_markings'] : '',
    markingsDescription: data?.['visible_markings_reason'] ? data['visible_markings_reason'] : '',
    isConvictedOfCrime: data?.['is_crime'] ? data['is_crime'] : '',
    crimeDescription: data?.['crime_description'] ? data['crime_description'] : '',
    hasRepExperience: (data?.['is_switchover'] || [0, 1].includes(data?.['is_switchover']))
      ? data['is_switchover'].toString()
      : '',
    [CURRENT_SITUATION_NAME]: data?.[CURRENT_SITUATION_NAME] ? data[CURRENT_SITUATION_NAME] : '',
    [SOURCE_OF_DISCOVERY_NAME]: data?.[SOURCE_OF_DISCOVERY_NAME] ? data[SOURCE_OF_DISCOVERY_NAME] : '',
    [REP_EXPERIENCES_DATA_NAME]: experiences,
    employmentData: employmentHistory,
    referenceData: referencesHistory,
    signature: data?.['signaturePicture'] ? data['signaturePicture'] : '',
    [LIMITED_LIABILITY_COMPANY]: data?.['limited_liability_company'] ? data['limited_liability_company'] : '',
    [EMPLOYER_IDENTIFICATION_NUMBER]: data?.['employer_identification_number'] ? data['employer_identification_number'] : '',
    [SOLICITOR_PERMIT_NUMBER]: data?.['solicitor_permit_number'] ? data['solicitor_permit_number'] : '',
    [SOLICITOR_EXPIRATION_DATE]: data?.['solicitor_expiration_date'] ? data['solicitor_expiration_date'] : '',
    // HR

    passportPicture: data?.['passportPicture'] ? data['passportPicture'] : '',
    passportExpirationDate: data?.['passport_expiration_date']
      ? formatDate(data['passport_expiration_date'])
      : '',
    driverLicense: data?.['driverLicensePicture']
      ? data['driverLicensePicture']
      : '',
    socialSecurityCard: data?.['socialSecurityCardPicture']
      ? data['socialSecurityCardPicture']
      : '',
    usesType: data?.['passportPicture']
      ? onboardingDataValues.PASSPORT_VALUE
      : data?.['socialSecurityCardPicture'] || data?.['socialSecurityCardPicture']
        ? onboardingDataValues.DL_AND_SOCIAL_VALUE
        : '',
    wotcSurveyCompleted: data?.['wotcSurveyCompleted'] ? data['wotcSurveyCompleted'] : false,
    w9Completed: data?.['w9_completed'] ? data['w9_completed'] : '',
    w9EnvelopeSent: data?.['w9EnvelopeSent'] ? data['w9EnvelopeSent'] : false,
    w9Clicked: !!data?.['w9EnvelopeSent'] || !!data?.['w9_completed'],
    w9Submitted: !!data?.['w9Picture'],
    previousW9Exists: !!data?.['previousW9Exists'],
    [USE_PREVIOUS_W9_NAME]: !!data?.[USE_PREVIOUS_W9_NAME],
    i9Completed: !!data?.['i9_completed'],
    i9EnvelopeSent: !!data?.['i9EnvelopeSent'],
    i9Clicked: !!data?.['i9EnvelopeSent'] || !!data?.['i9_completed'],
    i9Submitted: !!data?.['i9Picture'],

    [DIRECT_DEPOSIT_BANK_NAME]: data?.[DIRECT_DEPOSIT_BANK_NAME] ? data[DIRECT_DEPOSIT_BANK_NAME] : '',
    [DIRECT_DEPOSIT_ACCOUNT_NAME]: data?.[DIRECT_DEPOSIT_ACCOUNT_NAME] ? data[DIRECT_DEPOSIT_ACCOUNT_NAME] : '',
    [DIRECT_DEPOSIT_ACCOUNT_TYPE]: data?.[DIRECT_DEPOSIT_ACCOUNT_TYPE] ? data[DIRECT_DEPOSIT_ACCOUNT_TYPE] : '',
    [DIRECT_DEPOSIT_ROUTING_NUMBER]: data?.[DIRECT_DEPOSIT_ROUTING_NUMBER] ? data[DIRECT_DEPOSIT_ROUTING_NUMBER] : '',
    [DIRECT_DEPOSIT_ACCOUNT_NUMBER]: '',
    [DIRECT_DEPOSIT_CONFIRM_ACCOUNT_NUMBER]: '',

    //Approval
    [PENDING_APPROVAL_ITEMS_NAME]: data?.pending_approval_items ?? [],

    // Workday
    workerId: data?.['worker_id'],
    workdayId: data?.['workday_id'],
    workdayHireDate: data?.['workday_hire_date'] ? formatDate(data['workday_hire_date']) : '',
    workdayComplete: Boolean(data?.['workday_complete']),

    // Pronto
    prontoUserId: data?.['pronto_user_id'] ? String(data['pronto_user_id']) : '',

    isEditable: Boolean(data?.['is_editable']),

    [PROFILE_PICTURE_URL_NAME]: data?.['profile_img'] ?? '',
    [PASSPORT_PICTURE_URL_NAME]: data?.['passport_img'] ?? '',
    [DRIVER_LICENSE_URL_NAME]: data?.['dl_img'] ?? '',
    [SOCIAL_SECURITY_URL_NAME]: data?.['ss_img'] ?? '',

    // Clear local files when getting user data
    [PROFILE_PICTURE_LOCAL_FILE]: '',
    [PASSPORT_PICTURE_LOCAL_FILE]: '',
    [DRIVER_LICENSE_LOCAL_FILE]: '',
    [SOCIAL_SECURITY_PICTURE_LOCAL_FILE]: '',

    // Private, immutable data: should not be shown to the user or modified
    _: {
      [DIRECT_DEPOSIT_ACCOUNT_NUMBER]: data?.[DIRECT_DEPOSIT_ACCOUNT_NUMBER] ? data[DIRECT_DEPOSIT_ACCOUNT_NUMBER] : '',
    },
  };
};
